import { ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';
import { Metamodel } from 'app/model/base/model';
import { AzureDataLakeAnalyticsLinkedService } from 'app/model/linkedservice/azure-data-lake-analytics.linkedservice';
import { format } from 'app/shared/utils/string.util';
import { azureResourceSelectMethodOptions } from '../consts/metadata-consts';
import { ConnectionAuthKind, CredentialTypeOptions } from 'shell-models-lib';
import { DataSourceOptionKind, CommonResourceKind as ResourceKind } from '@powerquery/mashup-editor-client';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { GlobalVariables } from 'app/services/globals';
import { AuthenticationType } from 'app/interfaces/IAuthenticationType';
import { compact } from 'lodash';

const accountSelectionControls: ControlMetadata[] = [
    {
        type: 'radio-switch',
        nested: 1,
        label: stringResources.AzureDataLakeAnalytics_SelectionMode,
        options: azureResourceSelectMethodOptions,
        isLabelVertical: true,
        defaultValue: '{accountName != null ? "manual" : "azure"}',
        cases: [
            {
                caseValue: 'azure',
                children: [
                    {
                        type: 'azure-data-lake-analytics-account-select',
                        bind: {
                            adlaAccountName: 'accountName',
                            subscriptionId: 'subscriptionId',
                            resourceGroupName: 'resourceGroupName'
                        },
                        validators: {
                            adlaAccountName: [{ type: 'required', errorMessage: '' }]
                        }
                    }
                ]
            },
            {
                caseValue: 'manual',
                children: [
                    {
                        type: 'textbox',
                        bind: 'accountName',
                        label: stringResources.ModelName_AccountName,
                        required: true,
                        validators: [{ type: 'required', errorMessage: '' }],
                        isLabelVertical: true
                    },
                    {
                        type: 'textbox',
                        bind: 'subscriptionId',
                        label: stringResources.SubscriptionId,
                        isLabelVertical: true,
                        validators: [{ type: 'required', errorMessage: '' }],
                        required: true
                    },
                    {
                        type: 'textbox',
                        bind: 'resourceGroupName',
                        label: stringResources.ModelName_ResourceGroupName,
                        required: true,
                        validators: [{ type: 'required', errorMessage: '' }],
                        isLabelVertical: true
                    }
                ]
            }
        ]
    }
];

const servicePrincipalControls: ControlMetadata[] = [
    {
        type: 'tenant-textbox',
        bind: 'tenant',
        validators: [{ type: 'required', errorMessage: '' }],
        isLabelVertical: true,
        label: stringResources.ModelName_Tenant,
        required: true
    },
    {
        type: 'textbox',
        bind: 'servicePrincipalId',
        label: stringResources.ModelName_ServicePrincipalID,
        required: true,
        validators: [{ type: 'required', errorMessage: '' }],
        isLabelVertical: true,
        description:
            '<a target="_blank" href="https://docs.microsoft.com/azure/data-factory/transform-data-using-data-lake-analytics#azure-data-lake-analytics-linked-service">' +
            stringResources.LearnMore +
            '</a>',
        escapeDescription: false
    },
    {
        type: 'dynamic-akv-switcher',
        bind: 'servicePrincipalKey',
        label: stringResources.ModelName_ServicePrincipalKey,
        child: {
            type: 'password',
            label: stringResources.ModelName_ServicePrincipalKey,
            required: true,

            isLabelVertical: true
        },
        validators: [{ type: 'required', errorMessage: '' }],
        description: format(
            stringResources.AzureKeyVaultSecretName_Tooltip_Template,
            format(stringResources.ModelName_LinkedServiceTemplate, stringResources.AzureDataLakeAnalytics_DisplayText),
            stringResources.ModelName_ServicePrincipalKey
        )
    }
];

const authenticationControls: ControlMetadata = {
    type: 'select-switch',
    bind: 'authenticationType',
    label: stringResources.ModelName_AuthenticationType,
    options: !GlobalVariables.enableUamiAuthForScopeActivity
        ? [
              {
                  label: stringResources.AuthenticationType_ManagedServiceIdentity,
                  value: AuthenticationType.ManagedServiceIdentity
              },
              {
                  label: stringResources.AuthenticationType_ServicePrincipal,
                  value: AuthenticationType.ServicePrincipal
              }
          ]
        : [
              {
                  label: stringResources.AuthenticationType_ManagedServiceIdentity,
                  value: AuthenticationType.ManagedServiceIdentity
              },
              {
                  label: stringResources.AuthenticationType_ServicePrincipal,
                  value: AuthenticationType.ServicePrincipal
              },
              {
                  label: stringResources.AuthenticationType_UserAssignedManagedIdentity,
                  value: AuthenticationType.UserAssignedManagedIdentity
              }
          ],
    required: true,
    validators: [{ type: 'required', errorMessage: '' }],
    isLabelVertical: true,
    defaultValue: AuthenticationType.ServicePrincipal,
    cases: !GlobalVariables.enableUamiAuthForScopeActivity
        ? [
              {
                  caseValue: AuthenticationType.ServicePrincipal,
                  children: servicePrincipalControls
              },
              {
                  caseValue: AuthenticationType.ManagedServiceIdentity,
                  children: [
                      {
                          type: 'management-identity-info',
                          resourceType: stringResources.AzureDataLakeAnalytics_DisplayText,
                          link: 'https://docs.microsoft.com/azure/data-factory/transform-data-using-data-lake-analytics#azure-data-lake-analytics-linked-service'
                      }
                  ]
              }
          ]
        : [
              {
                  caseValue: AuthenticationType.ServicePrincipal,
                  children: servicePrincipalControls
              },
              {
                  caseValue: AuthenticationType.ManagedServiceIdentity,
                  children: [
                      {
                          type: 'management-identity-info',
                          resourceType: stringResources.AzureDataLakeAnalytics_DisplayText,
                          link: 'https://docs.microsoft.com/azure/data-factory/transform-data-using-data-lake-analytics#azure-data-lake-analytics-linked-service'
                      }
                  ]
              },
              {
                  caseValue: AuthenticationType.UserAssignedManagedIdentity,
                  children: [
                      {
                          type: 'credential-select',
                          bind: 'credential',
                          filterCredentialType: CredentialTypeOptions.UserAssignedManagedIdentity,
                          required: true,
                          validators: [{ type: 'required', errorMessage: '' }]
                      }
                  ]
              }
          ]
};

export const azureDataLakeAnalyticsMetadata: ILinkedServiceTypeMetadata = {
    type: Metamodel.modelFor(AzureDataLakeAnalyticsLinkedService).name,
    displayName: stringResources.AzureDataLakeAnalytics_DisplayText,
    supportedInTrident: true,
    isKnownInPipelineContext: true,
    pqoDataSourceOption: {
        kind: DataSourceOptionKind.Extension,
        resourceKind: 'DataLakeAnalytics' as ResourceKind
    },
    supportOPDG: GlobalVariables.enableOPDGForActivities,
    supportedAuthKindsInTrident: compact([
        ConnectionAuthKind.ServicePrincipal,
        GlobalVariables.enableWIAuthForAdlaAndAdls ? ConnectionAuthKind.WorkspaceIdentity : undefined
    ]),
    category: ['Azure'],
    icon: {
        className: 'svg-i-100x100-azure_datalakeanalytics'
    },
    isCompute: true,
    isFirstClass: true,
    copySinkTypes: [],
    copySourceTypes: [],
    controls: !GlobalVariables.enableMsiAuthForScopeActivity
        ? [...accountSelectionControls, ...servicePrincipalControls]
        : [...accountSelectionControls, authenticationControls]
};
